import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
} from "lucide-react";

function countWords(html) {
  if (!html) return 0;
  const text = html.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
  return text ? text.split(" ").filter(Boolean).length : 0;
}

function wordCountColor(n) {
  if (n >= 150) return "#4CAF50";
  if (n >= 100) return "#C8922A";
  return "#CC1A1A";
}

const PRESET_COLORS = [
  { hex: "#FFFFFF", label: "White" },
  { hex: "#CC1A1A", label: "Red" },
  { hex: "#C8922A", label: "Gold" },
  { hex: "#888888", label: "Grey" },
  { hex: "#FFDD57", label: "Yellow" },
];

function toHtml(val) {
  if (!val) return "";
  if (/<[a-z][\s\S]*>/i.test(val)) return val;
  return val
    .split(/\n\n+/)
    .map((p) => `<p>${p.trim().replace(/\n/g, "<br>")}</p>`)
    .join("");
}

export default function RichTextEditor({ value, onChange, testId }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
    ],
    content: toHtml(value),
    onUpdate({ editor: ed }) {
      onChange(ed.getHTML());
    },
  });

  const prevRef = useRef(value);
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (value === prevRef.current) return;
    prevRef.current = value;
    const incoming = toHtml(value);
    if (incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  return (
    <div className="tiptap-editor" data-testid={testId}>
      <div className="tiptap-toolbar flex-wrap">
        {/* Bold / Italic */}
        <ToolBtn label="B" title="Bold (Ctrl+B)" active={editor?.isActive("bold")}
          onClick={() => editor?.chain().focus().toggleBold().run()} />
        <ToolBtn label="I" title="Italic (Ctrl+I)" italic
          active={editor?.isActive("italic")}
          onClick={() => editor?.chain().focus().toggleItalic().run()} />

        <div className="tiptap-divider" />

        {/* Headings */}
        <ToolBtn label={<Heading2 className="w-4 h-4" />} title="Heading 2"
          active={editor?.isActive("heading", { level: 2 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} />
        <ToolBtn label={<Heading3 className="w-4 h-4" />} title="Heading 3"
          active={editor?.isActive("heading", { level: 3 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} />

        <div className="tiptap-divider" />

        {/* Lists */}
        <ToolBtn label={<List className="w-4 h-4" />} title="Bullet list"
          active={editor?.isActive("bulletList")}
          onClick={() => editor?.chain().focus().toggleBulletList().run()} />
        <ToolBtn label={<ListOrdered className="w-4 h-4" />} title="Numbered list"
          active={editor?.isActive("orderedList")}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()} />

        <div className="tiptap-divider" />

        {/* Alignment */}
        <ToolBtn label={<AlignLeft className="w-4 h-4" />} title="Align left"
          active={editor?.isActive({ textAlign: "left" })}
          onClick={() => editor?.chain().focus().setTextAlign("left").run()} />
        <ToolBtn label={<AlignCenter className="w-4 h-4" />} title="Align center"
          active={editor?.isActive({ textAlign: "center" })}
          onClick={() => editor?.chain().focus().setTextAlign("center").run()} />
        <ToolBtn label={<AlignRight className="w-4 h-4" />} title="Align right"
          active={editor?.isActive({ textAlign: "right" })}
          onClick={() => editor?.chain().focus().setTextAlign("right").run()} />
        <ToolBtn label={<AlignJustify className="w-4 h-4" />} title="Justify"
          active={editor?.isActive({ textAlign: "justify" })}
          onClick={() => editor?.chain().focus().setTextAlign("justify").run()} />

        <div className="tiptap-divider" />

        {/* Color swatches */}
        {PRESET_COLORS.map(({ hex, label }) => (
          <button
            key={hex}
            type="button"
            title={`${label} (${hex})`}
            onMouseDown={(e) => {
              e.preventDefault();
              const current = editor?.getAttributes("textStyle").color;
              if (current === hex) {
                editor?.chain().focus().unsetColor().run();
              } else {
                editor?.chain().focus().setColor(hex).run();
              }
            }}
            className="tiptap-btn"
            style={{ padding: "3px 4px" }}
          >
            <span
              style={{
                display: "block",
                width: 14,
                height: 14,
                background: hex,
                border: editor?.getAttributes("textStyle").color === hex
                  ? "2px solid #CC1A1A"
                  : "1px solid #555",
              }}
            />
          </button>
        ))}
      </div>

      <EditorContent editor={editor} />

      {(() => {
        const n = countWords(value);
        return (
          <p style={{ fontFamily: "monospace", fontSize: "11px", letterSpacing: "0.1em", marginTop: "6px", color: wordCountColor(n) }}>
            Word count: {n} word{n !== 1 ? "s" : ""}
          </p>
        );
      })()}
    </div>
  );
}

function ToolBtn({ label, title, active, italic: isItalic, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={`tiptap-btn${active ? " tiptap-btn-active" : ""}${isItalic ? " italic" : ""}`}
    >
      {label}
    </button>
  );
}
